"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Wifi, Check, RefreshCw } from "lucide-react";

const STEPS = [
  { time: "9:47 PM", event: { en: "WiFi drops. The POS switches to cash mode.", es: "WiFi se cae. El POS pasa a modo efectivo." }, color: "#DC2626", icon: "off" as const },
  { time: "9:47-10:03 PM", event: { en: "42 cash sales rung offline · saved on the device", es: "42 ventas en efectivo offline · guardadas en el dispositivo" }, color: "#F4722B", icon: "queue" as const },
  { time: "10:03 PM", event: { en: "WiFi back. Sync starts on its own. Cards work again.", es: "WiFi vuelve. El sync arranca solo. La tarjeta vuelve a funcionar." }, color: "#2ECC71", icon: "on" as const },
  { time: "10:04 PM", event: { en: "All 42 cash sales in your reports.", es: "Las 42 ventas en efectivo en tus reportes." }, color: "#2ECC71", icon: "done" as const },
];

export default function OfflineSyncScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Auto-sync timeline · example", "Timeline de auto-sync · ejemplo")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Signal back? ", "¿Señal de vuelta? ")}
            <span className="text-redy-charcoal/30">{t("Your cash sales sync on their own.", "Tus ventas en efectivo se sincronizan solas.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "The cash sales saved on the device write to your reports automatically. If one can't sync, the POS flags it for review instead of losing it — so there's no \"did that go through?\" doubt at close.",
              "Las ventas en efectivo guardadas en el dispositivo se escriben solas en tus reportes. Si alguna no puede sincronizar, el POS la marca para revisión en vez de perderla — así no hay \"¿se procesó?\" al cerrar."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[28px] p-7 md:p-8 max-w-[760px] mx-auto shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
              {t("Friday night · example", "Viernes noche · ejemplo")}
            </div>
            <div className="text-[10px] font-bold text-redy-secondary/55">
              {t("All clear", "Todo OK")}
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[18px] top-2 bottom-2 w-px bg-black/10" />

            <div className="space-y-4">
              {STEPS.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: 0.2 + 0.2 * i }}
                  className="flex gap-4 items-start relative"
                >
                  {/* Dot */}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 z-10"
                    style={{ backgroundColor: `${step.color}22`, border: `2px solid ${step.color}` }}
                  >
                    {step.icon === "off" && <Wifi className="w-4 h-4" style={{ color: step.color }} strokeWidth={2.5} />}
                    {step.icon === "queue" && <RefreshCw className="w-4 h-4" style={{ color: step.color }} strokeWidth={2.5} />}
                    {step.icon === "on" && <Wifi className="w-4 h-4" style={{ color: step.color }} strokeWidth={2.5} />}
                    {step.icon === "done" && <Check className="w-4 h-4" style={{ color: step.color }} strokeWidth={3} />}
                  </div>

                  <div className="flex-1 pt-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider tabular-nums" style={{ color: step.color }}>
                      {step.time}
                    </div>
                    <div className="text-[14px] font-black text-redy-charcoal leading-snug mt-0.5">
                      {t(step.event.en, step.event.es)}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Final stat */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-7 pt-6 border-t border-black/5 text-center"
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
              {t("Cash sales rung during the outage", "Ventas en efectivo durante el corte")}
            </div>
            <div
              className="text-[40px] font-black tabular-nums leading-none"
              style={{
                background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              $1,840
            </div>
            <div className="text-[12px] font-bold text-redy-secondary mt-1">
              {t("Example night. Sales you didn't have to turn away.", "Noche de ejemplo. Ventas que no tuviste que rechazar.")}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
